import { z } from 'zod';
import { ProcessStepRecordingSchema } from '../process-step-recording';
/**
 * Process Node Schemas (ADR 0003)
 *
 * Every process-graph node — recorded steps and events — lives in the step
 * collections, discriminated by `nodeKind`. These schemas describe the event
 * arm and the discriminated union over both arms.
 *
 * None of these schemas may use `.refine`/`.superRefine`: bliksy's
 * `resolveSubSchema` cannot walk refine wrappers when resolving deep paths.
 */ /**
 * Event Kind Schema
 *
 * Start-ness is identity, not derivable: a start event with nothing attached
 * is still the start event, and the Start Recording page gate must find it to
 * prompt the user.
 */ export const EventKindSchema = z.enum([
    'start',
    'intermediate'
]);
/**
 * Shared core of every event-node arm: the discriminator, identity and
 * content fields common to the live and draft variants. Each arm extends this
 * with only its keying — live (`process`) vs draft
 * (`processId`/`draftId`/`stableStepId`). Not exported: the arms below are the
 * public contract surface.
 */ const EventNodeCoreSchema = z.object({
    nodeKind: z.literal('event'),
    _id: z.string(),
    eventKind: EventKindSchema,
    eventType: z.string(),
    name: z.string(),
    description: z.string(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    deletedAt: z.string().nullable().optional()
});
/**
 * Process Event Node Schema
 *
 * An event node in the unified node collection. Event nodes have no inbound
 * edges (`previousStep` is always null/absent) and carry no `inputs[]` —
 * they are external triggers, not chain members. Who works first is not on
 * the event: it is whatever node carries an input on the start event.
 * Served by show-process's `events[]`; event docs must NEVER travel through
 * `ProcessStepRecordingDisplaySchema` (ADR 0003 — its fallback would leak
 * unvalidated docs).
 */ export const ProcessEventNodeSchema = EventNodeCoreSchema.extend({
    process: z.string()
});
/**
 * Process Draft Event Node Schema
 *
 * The draft arm of an event node: draft rows are keyed by `draftId` +
 * `processId` (not `process`) and carry `stableStepId` — the event's identity
 * across draft↔publish, exactly like draft steps. Everything else mirrors
 * {@link ProcessEventNodeSchema}. Travels as `GetProcessDraftResponse.events`
 * — served by the backend since issue 07.
 */ export const ProcessDraftEventNodeSchema = EventNodeCoreSchema.extend({
    processId: z.string(),
    draftId: z.string(),
    stableStepId: z.string()
});
/**
 * Process Node Schema
 *
 * Discriminated union over the two node arms sharing one collection. The step
 * arm's `nodeKind` defaults to `'step'`, so legacy step payloads without the
 * field still parse through `ProcessStepRecordingSchema` directly — the union
 * itself requires an explicit discriminator value.
 */ export const ProcessNodeSchema = z.discriminatedUnion('nodeKind', [
    ProcessStepRecordingSchema,
    ProcessEventNodeSchema
]);

//# sourceMappingURL=process-node.schema.js.map